import { Navigate, useLocation, type RouteObject } from 'react-router'
import { useAuth } from './auth/AuthContext'
import { Shell } from './components/Shell'
import { RouteError } from './components/ErrorBoundary'
import { RouteSkeleton } from './components/RouteSkeleton'
import { useI18n } from './i18n'
import { AuthLayout } from './pages/AuthLayout'
import { ForgotPage, LoginPage, ResetPage, SignupPage, VerifyPage } from './pages/AuthPages'
import { NotFoundPage } from './pages/NotFoundPage'
import { TranscriptionsPage } from './pages/TranscriptionsPage'

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { status } = useAuth()
  const { t } = useI18n()
  const location = useLocation()
  if (status === 'loading') return <p style={{ padding: '2rem' }} role="status">{t('common.loading')}</p>
  if (status === 'signedOut') {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }
  return <>{children}</>
}

function ReturnToBilling({ result }: { result: 'success' | 'canceled' }) {
  return <Navigate to={`/billing?checkout=${result}`} replace />
}

export const routes: RouteObject[] = [
  {
    element: <AuthLayout />,
    HydrateFallback: RouteSkeleton,
    errorElement: <RouteError />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/signup', element: <SignupPage /> },
      { path: '/verify', element: <VerifyPage /> },
      { path: '/forgot', element: <ForgotPage /> },
      { path: '/reset', element: <ResetPage /> },
    ],
  },
  {
    element: (
      <RequireAuth>
        <Shell />
      </RequireAuth>
    ),
    HydrateFallback: RouteSkeleton,
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <TranscriptionsPage /> },
      // The editor, keys and billing screens load on demand to keep the first paint small.
      {
        path: '/transcriptions/:id',
        HydrateFallback: RouteSkeleton,
        lazy: async () => ({ Component: (await import('./editor/EditorPage')).EditorPage }),
      },
      { path: '/keys', HydrateFallback: RouteSkeleton, lazy: async () => ({ Component: (await import('./pages/KeysPage')).KeysPage }) },
      { path: '/billing', HydrateFallback: RouteSkeleton, lazy: async () => ({ Component: (await import('./pages/BillingPage')).BillingPage }) },
      { path: '/admin', HydrateFallback: RouteSkeleton, lazy: async () => ({ Component: (await import('./pages/AdminPage')).AdminPage }) },
      { path: '/billing/success', element: <ReturnToBilling result="success" /> },
      { path: '/billing/cancel', element: <ReturnToBilling result="canceled" /> },
    ],
  },
  { path: '*', element: <NotFoundPage />, HydrateFallback: RouteSkeleton, errorElement: <RouteError /> },
]
